import { getBrowser } from 'livekit-client';
import UAParser from 'ua-parser-js';

const userAgent = navigator.userAgent;

export const getIsMobile = (): boolean => {
  // Primary: combined media query — the most reliable signal.
  // (pointer: coarse) = primary input is imprecise (touch/stylus).
  // (hover: none)     = primary input cannot hover (not a mouse).
  // Together they identify touch-primary devices while excluding Windows touch
  // laptops where the mouse remains primary (maxTouchPoints > 0 but pointer: fine).
  if (typeof window.matchMedia === 'function') {
    const coarse = matchMedia('(pointer: coarse)').matches;
    if (!coarse) return false;
    const noHover = matchMedia('(hover: none)').matches;
    if (noHover) return true;
    // Coarse pointer but hover-capable (rare: some stylus-first hybrid devices).
    // Fall through to maxTouchPoints as tiebreaker.
  }

  // Fallback: PointerEvent + maxTouchPoints (universally supported post-IE11).
  if ('maxTouchPoints' in navigator) return navigator.maxTouchPoints > 0;

  // Fallback: Touch Events API (legacy Android/iOS browsers).
  if ('ontouchstart' in window || 'TouchEvent' in window) return true;

  // Last resort: UA sniffing for known mobile platforms.
  return (
    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(userAgent) ||
    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(userAgent)
  );
};

export const isWebView = /wv/.test(userAgent);
export const canPlayVideo = !!document.createElement('video').canPlayType;
export const isWebSocketBlocked = window.WebSocket.CLOSING !== 2;
export const isWebRTCSupported = !!navigator.mediaDevices && !!window.RTCPeerConnection && !!window.WebSocket;

export const isBrowserSupported = !isWebView && canPlayVideo && isWebRTCSupported && !isWebSocketBlocked;

export const isScreenSharingSupported = !!navigator.mediaDevices && !!navigator.mediaDevices.getDisplayMedia;
// iOS exposes PushManager only as a PWA (standalone mode). In a regular browser
// tab on any iOS version, Web Push silently does not work.
const isIOSNotStandalone =
  /iPad|iPhone|iPod/.test(userAgent) && !(navigator as Navigator & { standalone?: boolean }).standalone;

export const isWebPushNotificationSupported =
  'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window && !isIOSNotStandalone;

export const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
export const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const isSafariBased = () => {
  const browser = getBrowser();
  return browser?.name === 'Safari' || browser?.os === 'iOS';
};

export const getIsIOSUnsupported = () => {
  const parser = new UAParser(navigator.userAgent);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return false;

  const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (!match) return false;

  const iOSVersion = parseInt(match[1], 10);
  const browserName = parser.getBrowser().name || '';
  const isUnsupportedBrowser = /Chrome|Firefox/.test(browserName);

  return iOSVersion < 17 && isUnsupportedBrowser;
};

export const recommendedBrowsers = [
  {
    title: 'Chrome',
    link: 'https://www.google.com/chrome/?brand=BNSD&gclid=Cj0KCQiA5OuNBhCRARIsACgaiqVZfONWKzf7LB7qhMnKfSD4gEs1hRmRc1CRGISWd_zggjN7R4JqnR4aAhhqEALw_wcB&gclsrc=aw.ds',
  },
  {
    title: 'Opera',
    link: 'https://www.opera.com/?utm_campaign=%2300%20-%20WW%20-%20Search%20-%20EN%20-%20Branded&gclid=Cj0KCQiA5OuNBhCRARIsACgaiqVqn0tYlV9KRL_14YbaB64dySh0hhq_mMOXdo98s70HkAqEN5r7gTkaAlnBEALw_wcB',
  },
];
